var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useEffect, useRef } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { TablePagination } from 'root/src/client/v2/common/components/TablePagination';
// Wraps a table with a pagination bar on the top (only when there is more than one page, to avoid
// cluttering single-page tables) and the bottom. Whenever the page changes it (1) scrolls the top of
// the table back into view below the fixed navbar, so after paging from the bottom bar you land at the
// start of the new page instead of its end, and (2) pulses the table content (a brief fade) so paging
// animates instead of snapping. The pulse uses animation controls (not a keyed remount), so any inner
// tab carousel keeps working. Skips the initial mount and respects reduced motion.
export const PaginatedTable = (_a) => {
    var { children, scrollAnchorClassName = 'scroll-mt-[88px]' } = _a, pagination = __rest(_a, ["children", "scrollAnchorClassName"]);
    const topRef = useRef(null);
    const previousPage = useRef(pagination.page);
    const contentControls = useAnimationControls();
    useEffect(() => {
        var _a;
        // Only react to an ACTUAL page change, never the initial mount (otherwise entering a page that
        // has this table scrolls straight to it). A boolean "skip first mount" ref is defeated by React
        // StrictMode, which double-invokes the effect on the same instance in dev: the first run flips
        // the ref and the second run then passes the guard and scrolls. Comparing the previous page
        // value is StrictMode-safe - on mount (and on the StrictMode re-run) the page is unchanged, so
        // it no-ops; it only fires once the user actually pages.
        if (previousPage.current === pagination.page) {
            return;
        }
        previousPage.current = pagination.page;
        const prefersReduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        (_a = topRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
        if (!prefersReduced) {
            contentControls.set({ opacity: 0.4 });
            void contentControls.start({ opacity: 1, transition: { duration: 0.22, ease: 'easeOut' } });
        }
    }, [pagination.page, contentControls]);
    return (<>
			{/* Scroll anchor: scroll-mt clears the fixed bars so the table top is not hidden under them. */}
			<div ref={topRef} aria-hidden className={scrollAnchorClassName}/>
			{pagination.pageCount > 1 && <TablePagination {...pagination} className="mb-3 mt-0"/>}
			<motion.div animate={contentControls}>{children}</motion.div>
			<TablePagination {...pagination}/>
		</>);
};
